import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth, formatApiError } from "../lib/auth";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { ImageUpload } from "../components/brand/ImageUpload";
import { formatMoney } from "../lib/brand";
import { hydrateCart, setQuantity, removeFromCart, clearCart } from "../lib/cart";
import { toast } from "sonner";
import { CheckCircle, Storefront, Trash } from "@phosphor-icons/react";

const STAGE_CART = "cart";
const STAGE_PAYMENT = "payment";
const STAGE_DONE = "done";

export default function Checkout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0, delivery_fee: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState(STAGE_CART);
  const [address, setAddress] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    line1: "",
    line2: "",
    city: "",
    province: "",
    postal_code: "",
    notes: "",
  });
  const [customerEmail, setCustomerEmail] = useState(user?.email || "");
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [screenshot, setScreenshot] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const reload = async () => {
    setLoading(true);
    const c = await hydrateCart();
    setCart(c);
    setLoading(false);
  };

  useEffect(() => { reload(); api.get("/settings/payment").then(({ data }) => setSettings(data)); }, []);

  const placeOrder = async (e) => {
    e?.preventDefault?.();
    if (cart.items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!address.full_name || !address.phone || !address.line1 || !address.city) {
      toast.error("Please fill in your delivery address");
      return;
    }
    if (!customerEmail) { toast.error("Email is required"); return; }
    setSubmittingOrder(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_address: address,
        customer_email: customerEmail,
      });
      setOrder(data);
      setStage(STAGE_PAYMENT);
      toast.success("Order created. Complete payment to finish.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmittingOrder(false); }
  };

  const submitPayment = async () => {
    if (!screenshot) { toast.error("Upload your payment screenshot"); return; }
    setSubmittingPayment(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/submit-payment`, { screenshot_url: screenshot });
      setOrder(data);
      clearCart();
      setStage(STAGE_DONE);
      toast.success("Payment submitted. We're reviewing your order.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmittingPayment(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505]"><BrandHeader /><div className="px-6 py-16 text-white/60">Loading…</div></div>;

  if (stage === STAGE_DONE && order) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gold-gradient text-black mb-4">
            <CheckCircle size={28} weight="duotone" />
          </div>
          <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Order received</p>
          <h1 className="font-display text-4xl text-white mt-2">Thank you.</h1>
          <p className="text-white/60 mt-3">Your reference code is</p>
          <p className="font-mono text-2xl text-[#D4AF37] tracking-[0.3em] mt-2" data-testid="checkout-done-ref">{order.ref_code}</p>
          <p className="text-white/55 mt-6 text-sm">We're verifying your payment. You'll be notified when your order moves to Processing.</p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Link to="/shop" className="tsc-btn-outline">Back to shop</Link>
            {user && <Link to="/me" className="tsc-btn-gold">Go to dashboard</Link>}
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (stage === STAGE_PAYMENT && order && settings) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <BrandHeader />
        <section className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center mb-8 rise-in">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Complete payment</p>
            <h1 className="font-display text-4xl text-white mt-2">Pay {formatMoney(order.total)}</h1>
            <p className="text-white/55 mt-2">Reference: <span className="font-mono text-[#D4AF37] tracking-[0.25em]" data-testid="checkout-ref-code">{order.ref_code}</span></p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="tsc-card p-6" data-testid="checkout-gcash-card">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 1 · Pay via GCash</p>
              <div className="mt-4 bg-white p-3 rounded-md inline-block">
                {settings.gcash_qr_url ? (
                  <img src={settings.gcash_qr_url} alt="GCash QR" className="w-56 h-56 object-contain" />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center text-black/50 text-xs text-center px-4">QR not configured. Use the GCash number below.</div>
                )}
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                {settings.gcash_account_name && <p className="text-white/70"><span className="text-white/40 mr-2">Name</span>{settings.gcash_account_name}</p>}
                {settings.gcash_number && <p className="text-white/70"><span className="text-white/40 mr-2">GCash #</span><span className="font-mono">{settings.gcash_number}</span></p>}
              </div>
              {settings.shop_payment_instructions && (
                <p className="text-white/55 text-xs mt-5 whitespace-pre-wrap leading-relaxed">{settings.shop_payment_instructions}</p>
              )}
            </div>
            <div className="tsc-card p-6">
              <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Step 2 · Upload screenshot</p>
              <p className="text-white/60 text-sm mt-2">JPG / PNG / WEBP only. Max 5MB.</p>
              <div className="mt-4">
                <ImageUpload value={screenshot} onChange={setScreenshot} aspect="aspect-[3/4]" testId="checkout-screenshot" />
              </div>
              <button onClick={submitPayment} disabled={!screenshot || submittingPayment} className="tsc-btn-gold w-full mt-5" data-testid="checkout-submit-payment">
                {submittingPayment ? "Submitting…" : "Submit Payment"}
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // STAGE_CART
  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="max-w-5xl mx-auto px-6 py-10">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Checkout</p>
        <h1 className="font-display text-4xl text-white mt-2">Your cart</h1>

        {cart.items.length === 0 ? (
          <div className="text-center py-20 mt-8 border border-dashed border-white/10 rounded-md" data-testid="cart-empty">
            <Storefront size={36} className="text-white/30 mx-auto" weight="duotone" />
            <p className="text-white/60 mt-3">Your cart is empty.</p>
            <Link to="/shop" className="tsc-btn-outline inline-block mt-5">Browse the shop</Link>
          </div>
        ) : (
          <form onSubmit={placeOrder} className="grid lg:grid-cols-3 gap-8 mt-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="tsc-card divide-y divide-white/5" data-testid="cart-items">
                {cart.items.map((it) => (
                  <div key={it.product_id} className="p-4 flex items-center gap-4">
                    <div className="w-20 h-20 rounded bg-[#0a0a0a] overflow-hidden flex-shrink-0">
                      {it.product.featured_photo ? <img src={it.product.featured_photo} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/shop/${it.product_id}`} className="text-white hover:text-[#F9E596] font-medium truncate block">{it.product.title}</Link>
                      <p className="text-white/45 text-xs">{formatMoney(it.product.price)} each</p>
                    </div>
                    <div className="flex items-center border border-white/10 rounded">
                      <button type="button" onClick={() => setQuantity(it.product_id, it.quantity - 1) || reload()} className="px-3 py-1.5 text-white/70" data-testid={`cart-dec-${it.product_id}`}>−</button>
                      <span className="px-3 py-1.5 text-white text-sm min-w-[2.5rem] text-center">{it.quantity}</span>
                      <button type="button" onClick={() => setQuantity(it.product_id, it.quantity + 1) || reload()} className="px-3 py-1.5 text-white/70" data-testid={`cart-inc-${it.product_id}`}>+</button>
                    </div>
                    <p className="text-white font-medium w-24 text-right">{formatMoney(it.line_total)}</p>
                    <button type="button" onClick={() => { removeFromCart(it.product_id); reload(); }} className="text-white/40 hover:text-red-400" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash size={18} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="tsc-card p-6">
                <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Delivery address</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <input required value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} className="tsc-input" placeholder="Full name" data-testid="addr-full-name" />
                  <input required value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="tsc-input" placeholder="Phone" data-testid="addr-phone" />
                  <input required value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} type="email" className="tsc-input sm:col-span-2" placeholder="Email for order updates" data-testid="addr-email" />
                  <input required value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="tsc-input sm:col-span-2" placeholder="Address line 1" data-testid="addr-line1" />
                  <input value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="tsc-input sm:col-span-2" placeholder="Address line 2 (optional)" />
                  <input required value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="tsc-input" placeholder="City" data-testid="addr-city" />
                  <input value={address.province} onChange={(e) => setAddress({ ...address, province: e.target.value })} className="tsc-input" placeholder="Province" />
                  <input value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} className="tsc-input" placeholder="Postal code" />
                  <textarea rows="2" value={address.notes} onChange={(e) => setAddress({ ...address, notes: e.target.value })} className="tsc-input sm:col-span-2" placeholder="Delivery notes (optional)" />
                </div>
              </div>
            </div>

            <aside className="space-y-3">
              <div className="tsc-card p-6" data-testid="cart-summary">
                <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Order summary</p>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-white/70"><span>Subtotal</span><span data-testid="cart-subtotal">{formatMoney(cart.subtotal)}</span></div>
                  <div className="flex justify-between text-white/70"><span>Delivery fee</span><span data-testid="cart-delivery">{formatMoney(cart.delivery_fee)}</span></div>
                  <div className="flex justify-between text-white text-base font-medium border-t border-white/5 pt-3 mt-3"><span>Total</span><span data-testid="cart-total">{formatMoney(cart.total)}</span></div>
                </div>
                <p className="text-white/35 text-[11px] mt-4">Totals are verified server-side at checkout.</p>
                <button type="submit" disabled={submittingOrder} className="tsc-btn-gold w-full mt-5" data-testid="checkout-place-order">
                  {submittingOrder ? "Placing order…" : "Place order"}
                </button>
              </div>
              <Link to="/shop" className="block text-center text-[11px] tracking-luxe uppercase text-white/55 hover:text-white">Continue shopping</Link>
            </aside>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
